import type { ReactNode } from 'react';
import type {
  ImpersonateLocalStorage,
  UserLocalStorage,
} from '../../types/localStorage';

import { useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useParams, useSearchParams } from 'react-router';
import useLocalStorageState from 'use-local-storage-state';
import useSessionStorageState from 'use-session-storage-state';

import { getEnv } from '../../lib/env';
import { isUserRoleAdmin } from '../../lib/helpers';
import { detectLanguage } from '../../lib/language';

import { ModalProvider } from '../../contexts/ModalContext';

import LoaderPage from '../../pages/LoaderPage/LoaderPage';
import { useLoggedUser } from './useLoggedUser';

interface ProtectedRouteProps {
  /**
   * The content to render if the user is authenticated.
   */
  children?: ReactNode;
  /**
   * The content to render if the user is not authenticated.
   */
  redirect?: ReactNode;
  /**
   * The test ID to use for the component.
   */
  testId?: string;
}

const ProtectedRoute = ({ children, redirect }: ProtectedRouteProps) => {
  const { t } = useTranslation();
  const [user] = useLocalStorageState<UserLocalStorage>('cms.user');
  const [impersonate, , { removeItem: removeImpersonation }] =
    useSessionStorageState<ImpersonateLocalStorage>('cms.impersonate');
  const { lang } = useParams();
  const target = lang ?? detectLanguage();

  const [searchParams] = useSearchParams();
  const redirect_uri = searchParams.get('redirect_uri');

  const { isLoading: userLoading, user: userData } = useLoggedUser();

  useEffect(() => {
    const isRoleAdmin = isUserRoleAdmin(user);
    if (!isRoleAdmin && impersonate) removeImpersonation();
  }, [impersonate, removeImpersonation, user]);

  const removeImpersonationOnError = useCallback(() => {
    setTimeout(() => {
      toast.error(t('Global.UserNotAuthenticated'));
      removeImpersonation();
    });
  }, [removeImpersonation, t]);

  let currentLocation = window.location.href;
  const publicUrl = (getEnv('PUBLIC_URL') || window.location.origin).replace(
    /\/{0,10}$/,
    '',
  );
  if (currentLocation.replace(/\/{0,10}$/, '') === publicUrl) {
    currentLocation = '';
  }
  if (redirect_uri && user?.token) {
    const url = new URL(redirect_uri);

    if (url.host === window.location.host) {
      return <Navigate to={url.pathname + url.search} />;
    }

    return (
      <Navigate
        to={'/en/access-api-key'}
        state={{
          redirect_uri,
          response_type: searchParams.get('response_type'),
          key_type: searchParams.get('key_type'),
          application_name: searchParams.get('application_name'),
        }}
      />
    );
  }

  if (user) {
    if (user.data?.limits_plan?.free_space_limit === undefined) {
      return <Navigate to={'/logout?reason=new-system-version'} replace />;
    } else if (userLoading) {
      return (
        <ModalProvider>
          <LoaderPage />
        </ModalProvider>
      );
    } else if (!userData) {
      if (impersonate) {
        removeImpersonationOnError();
        return;
      }
      return <Navigate to={'/logout?reason=auth-error'} replace />;
    } else if (user.token) {
      return children ? children : <Outlet />;
    }
  } else if (redirect) {
    return redirect;
  }
  return (
    <Navigate
      to={
        !currentLocation
          ? `/${target}/login`
          : `/${target}/login?redirect_uri=${encodeURIComponent(currentLocation)}`
      }
      replace
    />
  );
};

export default ProtectedRoute;
